
import { useEffect, useState } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { DataTable, Column } from "@/components/ui/data-table"
import { courseService } from "@/services/course.service"
import { Category } from "@/types/api"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { Edit2, Trash2, Plus, Loader2 } from "lucide-react"
import { CategoryDialog } from "@/components/courses/CategoryDialog"
import { useToast } from "@/hooks/use-toast"
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog"

export default function CategoriesPage() {
    const [categories, setCategories] = useState<Category[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [page, setPage] = useState(1)
    const [totalCount, setTotalCount] = useState(0)
    const [editCategory, setEditCategory] = useState<Category | null>(null)
    const [isDialogOpen, setIsDialogOpen] = useState(false)
    const [deleteCategorySlug, setDeleteCategorySlug] = useState<string | null>(null)
    const [isDeleting, setIsDeleting] = useState(false)

    const { toast } = useToast()
    const PAGE_SIZE = 10

    const fetchCategories = async () => {
        setIsLoading(true)
        try {
            const response = await courseService.getCategories({
                limit: PAGE_SIZE,
                offset: (page - 1) * PAGE_SIZE,
            })
            console.log('Categories loaded:', response.results.length, response.results)
            setCategories(response.results)
            setTotalCount(response.count)
        } catch (error) {
            console.error(error)
            toast({
                title: "Ошибка",
                description: "Не удалось загрузить категории",
                variant: "destructive",
            })
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        fetchCategories()
    }, [page])

    const handleDelete = async () => {
        console.log(deleteCategorySlug)
        if (!deleteCategorySlug) return
        setIsDeleting(true)
        try {
            await courseService.deleteCategory(deleteCategorySlug)
            toast({
                title: "Успешно",
                description: "Категория успешно удалена",
            })
            fetchCategories()
        } catch (error) {
            console.error(error)
            toast({
                title: "Ошибка",
                description: "Не удалось удалить категорию",
                variant: "destructive",
            })
        } finally {
            setIsDeleting(false)
            setDeleteCategorySlug(null)
        }
    }

    const handleEdit = (category: Category) => {
        setEditCategory(category)
        setIsDialogOpen(true)
    }

    const handleCreate = () => {
        setEditCategory(null)
        setIsDialogOpen(true)
    }

    const columns: Column<Category>[] = [
        {
            header: "Иконка",
            cell: (cat) => (
                <Avatar className="h-10 w-10">
                    <AvatarImage src={cat.icon || undefined} />
                    <AvatarFallback>{cat.title.charAt(0)}</AvatarFallback>
                </Avatar>
            ),
        },
        {
            header: "Название",
            accessorKey: "title",
        },
        {
            header: "Слаг",
            accessorKey: "slug",
            className: "text-gray-500",
        },
        {
            header: "Действия",
            className: "text-right",
            cell: (cat) => (
                <div className="flex justify-end gap-2">
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEdit(cat)}
                    >
                        <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="text-red-500 hover:text-red-700"
                        onClick={(e) => {
                            e.stopPropagation()
                            console.log('Delete button clicked for:', cat.slug)
                            setDeleteCategorySlug(cat.slug)
                        }}
                    >
                        <Trash2 className="h-4 w-4" />
                    </Button>
                </div>
            ),
        },
    ]

    return (
        <div>
            <div className="mb-8 flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">Категории</h1>
                    <p className="text-gray-600 mt-1">Управление категориями курсов.</p>
                </div>
                <Button onClick={handleCreate}>
                    <Plus className="mr-2 h-4 w-4" />
                    Создать категорию
                </Button>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle>Список категорий</CardTitle>
                </CardHeader>
                <CardContent>
                    <DataTable
                        columns={columns}
                        data={categories}
                        isLoading={isLoading}
                        pageCount={Math.ceil(totalCount / PAGE_SIZE)}
                        currentPage={page}
                        onPageChange={setPage}
                    />
                </CardContent>
            </Card>

            <CategoryDialog
                category={editCategory}
                open={isDialogOpen}
                onOpenChange={setIsDialogOpen}
                onSuccess={fetchCategories}
            />

            <AlertDialog open={!!deleteCategorySlug} onOpenChange={(open) => !open && setDeleteCategorySlug(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Вы уверены?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Это действие нельзя отменить.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={isDeleting}>Отмена</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} disabled={isDeleting} className="bg-red-600 hover:bg-red-700">
                            {isDeleting ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Удаление...
                                </>
                            ) : (
                                "Удалить"
                            )}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    )
}
